// Photo Upload Utility
// Handles uploading job photos to Firebase Storage

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Compress an image file before upload
 * @param {File} file - Original image file
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>} - Compressed image blob
 */
async function compressImage(file, maxWidth = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down if needed
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
}

/**
 * Upload a photo to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} jobId - The job ID this photo belongs to
 * @param {string} photoType - Type: before | after | damage_existing | damage_new
 * @param {string} employeeId - ID of employee uploading
 * @returns {Promise<Object>} - Upload result with URL and metadata
 */
export async function uploadJobPhoto(file, jobId, photoType, employeeId) {
  try {
    // Compress the image
    const compressedBlob = await compressImage(file);
    
    // Generate unique filename
    const timestamp = Date.now();
    const filename = `${photoType}_${timestamp}.jpg`;
    const path = `jobs/${jobId}/photos/${filename}`;
    
    // Create storage reference
    const storageRef = ref(storage, path);
    
    // Upload the file
    const snapshot = await uploadBytes(storageRef, compressedBlob, {
      contentType: 'image/jpeg',
      customMetadata: {
        jobId,
        photoType,
        employeeId,
        uploadedAt: new Date().toISOString()
      }
    });
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Create photo metadata object
    const photoData = {
      id: `photo_${timestamp}`,
      url: downloadURL,
      path: path,
      type: photoType,
      employeeId,
      uploadedAt: new Date().toISOString(),
      filename
    };
    
    // Update the job document with the new photo
    const jobRef = doc(db, 'jobs', jobId);
    await updateDoc(jobRef, {
      photos: arrayUnion(photoData),
      updatedAt: new Date().toISOString()
    });
    
    return {
      success: true,
      photo: photoData
    };
    
  } catch (error) {
    console.error('Photo upload error:', error);
    throw error;
  }
}

/**
 * Upload multiple photos at once
 * @param {FileList|Array} files - Array of files to upload
 * @param {string} jobId - The job ID
 * @param {string} photoType - Type of photos
 * @param {string} employeeId - Employee ID
 * @returns {Promise<Array>} - Array of upload results
 */
export async function uploadMultiplePhotos(files, jobId, photoType, employeeId) {
  const results = [];
  const errors = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const result = await uploadJobPhoto(files[i], jobId, photoType, employeeId);
      results.push(result);
    } catch (error) {
      errors.push({ file: files[i].name, error: error.message });
    }
  }
  
  return {
    success: errors.length === 0,
    uploaded: results,
    errors
  };
}

/**
 * Upload damage report with photos
 * @param {Object} params - Damage report params
 */
export async function uploadDamageReport({
  jobId,
  employeeId,
  damageType, // 'existing' | 'new'
  description,
  photos // FileList or array of files
}) {
  const photoType = damageType === 'existing' ? 'damage_existing' : 'damage_new';
  
  // Upload photos first
  const photoResults = await uploadMultiplePhotos(photos, jobId, photoType, employeeId);
  
  // Create damage report object
  const damageReport = {
    id: `damage_${Date.now()}`,
    type: damageType,
    description,
    photos: photoResults.uploaded.map(r => r.photo),
    reportedBy: employeeId,
    reportedAt: new Date().toISOString(),
    status: 'pending' // pending | reviewed | resolved
  };
  
  // Update job with damage report
  const jobRef = doc(db, 'jobs', jobId);
  await updateDoc(jobRef, {
    damageReports: arrayUnion(damageReport),
    hasDamageReport: true,
    updatedAt: new Date().toISOString()
  });
  
  return {
    success: true,
    damageReport,
    photoResults
  };
}

/**
 * Get all photos for a job
 * @param {Object} job - Job object with photos array
 * @param {string} filterType - Optional: filter by photo type
 */
export function getJobPhotos(job, filterType = null) {
  if (!job?.photos) return [];
  
  if (filterType) {
    return job.photos.filter(p => p.type === filterType);
  }
  
  return job.photos;
}

/**
 * Check if required photos are taken
 * @param {Object} job - Job object
 * @returns {Object} - Status of required photos
 */
export function checkRequiredPhotos(job) {
  const photos = job?.photos || [];
  
  return {
    hasBefore: photos.some(p => p.type === 'before'),
    hasAfter: photos.some(p => p.type === 'after'),
    beforeCount: photos.filter(p => p.type === 'before').length,
    afterCount: photos.filter(p => p.type === 'after').length,
    damageCount: photos.filter(p => p.type.startsWith('damage')).length
  };
}

/**
 * Delete a property photo from Firebase Storage
 * @param {string} photoUrl - The download URL of the photo to delete
 * @returns {Promise<boolean>} - True if deletion was successful
 */
export async function deletePropertyPhoto(photoUrl) {
  try {
    const storageRef = ref(storage, photoUrl);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    // If the file doesn't exist in storage, still allow clearing the reference
    if (error.code === 'storage/object-not-found') {
      console.warn('Photo not found in storage, clearing reference anyway');
      return true;
    }
    console.error('Property photo delete error:', error);
    throw error;
  }
}

/**
 * Delete a photo from a job
 * Removes the file from Firebase Storage and updates the job document
 * @param {string} jobId - The job ID
 * @param {Object} photo - The photo object to delete (must have path or id)
 * @returns {Promise<Object>} - Result of the deletion
 */
export async function deleteJobPhoto(jobId, photo) {
  try {
    // Delete from Firebase Storage if path exists
    if (photo.path) {
      try {
        const storageRef = ref(storage, photo.path);
        await deleteObject(storageRef);
      } catch (storageErr) {
        // File may already be deleted from storage, continue with Firestore cleanup
        console.warn('Storage delete warning (file may not exist):', storageErr.code);
      }
    }

    // Remove from the job's photos array in Firestore
    const jobRef = doc(db, 'jobs', jobId);
    const jobSnap = await getDoc(jobRef);
    if (!jobSnap.exists()) {
      throw new Error('Job not found');
    }

    const jobData = jobSnap.data();
    const updatedPhotos = (jobData.photos || []).filter(p => {
      if (photo.id && p.id) return p.id !== photo.id;
      if (photo.path && p.path) return p.path !== photo.path;
      if (photo.url && p.url) return p.url !== photo.url;
      return true;
    });

    await updateDoc(jobRef, {
      photos: updatedPhotos,
      updatedAt: new Date().toISOString()
    });

    return { success: true, remainingPhotos: updatedPhotos };
  } catch (error) {
    console.error('Photo delete error:', error);
    throw error;
  }
}

/**
 * Upload a property photo to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} propertyId - The property ID
 * @returns {Promise<string>} - Download URL of the uploaded photo
 */
export async function uploadPropertyPhoto(file, propertyId) {
  try {
    const compressedBlob = await compressImage(file, 1200, 0.85);
    
    const timestamp = Date.now();
    const filename = `property_${timestamp}.jpg`;
    const path = `properties/${propertyId}/photos/${filename}`;
    
    const storageRef = ref(storage, path);
    
    const snapshot = await uploadBytes(storageRef, compressedBlob, {
      contentType: 'image/jpeg',
      customMetadata: {
        propertyId,
        uploadedAt: new Date().toISOString()
      }
    });
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Property photo upload error:', error);
    throw error;
  }
}
